import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ShieldAlert, Phone, MessageCircle, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/_authenticated/evento/$eventId/seguridad")({
  head: () => ({
    meta: [
      { title: "Roster de emergencia — Brulo" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SafetyRosterPage,
});

type EventRow = { id: string; name: string; event_date: string; event_time: string; organizer_id: string };
type Athlete = { user_id: string; full_name: string | null };
type Status = "green" | "yellow" | "red";
type Profile = {
  blood_type: string | null;
  allergies: string | null;
  medical_conditions: string | null;
  medications: string | null;
  insurance_provider: string | null;
  insurance_policy_number: string | null;
  preferred_hospital: string | null;
  notes: string | null;
};
type Contact = {
  id: string;
  full_name: string;
  relationship: string;
  phone: string;
  is_whatsapp: boolean | null;
  priority: number;
};

type Row = {
  athlete: Athlete;
  status: Status;
  profile: Profile | null;
  contacts: Contact[];
};

function SafetyRosterPage() {
  const { eventId } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventRow | null>(null);
  const [access, setAccess] = useState<boolean>(false);
  const [windowOpen, setWindowOpen] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: ev } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,organizer_id")
        .eq("id", eventId)
        .maybeSingle();
      if (cancelled) return;
      const evt = (ev as EventRow | null) ?? null;
      setEvent(evt);
      if (!evt) {
        setLoading(false);
        return;
      }

      const isOrganizer = evt.organizer_id === user.id;
      let hasRole = false;
      if (!isOrganizer) {
        const { data: role } = await supabase
          .from("event_safety_roles")
          .select("id")
          .eq("event_id", eventId)
          .eq("user_id", user.id)
          .maybeSingle();
        hasRole = !!role;
      }
      const allowed = isOrganizer || hasRole;
      setAccess(allowed);
      if (!allowed) {
        setLoading(false);
        return;
      }

      const { data: openData } = await supabase.rpc("event_safety_window_open", { p_event_id: eventId });
      const isOpen = !!openData;
      setWindowOpen(isOpen);
      if (!isOpen) {
        setLoading(false);
        return;
      }

      // Load inscritos
      const { data: ins } = await supabase
        .from("inscripciones")
        .select("usuario_id")
        .eq("evento_id", eventId);
      const userIds = (ins ?? []).map((r: { usuario_id: string }) => r.usuario_id);

      if (userIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const [profilesRes, consentsRes, emProfilesRes, emContactsRes] = await Promise.all([
        supabase.from("profiles").select("id,full_name").in("id", userIds),
        supabase
          .from("event_ice_consents")
          .select("user_id,revoked_at")
          .eq("event_id", eventId)
          .in("user_id", userIds),
        supabase
          .from("emergency_profiles")
          .select("user_id,blood_type,allergies,medical_conditions,medications,insurance_provider,insurance_policy_number,preferred_hospital,notes")
          .in("user_id", userIds),
        supabase
          .from("emergency_contacts")
          .select("id,user_id,full_name,relationship,phone,is_whatsapp,priority")
          .in("user_id", userIds)
          .order("priority", { ascending: true }),
      ]);

      const nameMap = new Map<string, string | null>();
      (profilesRes.data ?? []).forEach((p: { id: string; full_name: string | null }) =>
        nameMap.set(p.id, p.full_name),
      );
      const consentMap = new Map<string, boolean>();
      (consentsRes.data ?? []).forEach((c: { user_id: string; revoked_at: string | null }) => {
        if (!c.revoked_at) consentMap.set(c.user_id, true);
      });
      const profileMap = new Map<string, Profile>();
      (emProfilesRes.data ?? []).forEach((p: { user_id: string } & Profile) => {
        const { user_id, ...rest } = p;
        profileMap.set(user_id, rest);
      });
      const contactsMap = new Map<string, Contact[]>();
      (emContactsRes.data ?? []).forEach((c: { user_id: string } & Contact) => {
        const list = contactsMap.get(c.user_id) ?? [];
        list.push({
          id: c.id,
          full_name: c.full_name,
          relationship: c.relationship,
          phone: c.phone,
          is_whatsapp: c.is_whatsapp,
          priority: c.priority,
        });
        contactsMap.set(c.user_id, list);
      });

      const built: Row[] = userIds.map((uid) => {
        const consented = consentMap.get(uid) === true;
        const profile = profileMap.get(uid) ?? null;
        const contacts = contactsMap.get(uid) ?? [];
        let status: Status = "red";
        if (consented) {
          const hasProfileData =
            profile &&
            (profile.blood_type ||
              profile.allergies ||
              profile.medical_conditions ||
              profile.medications);
          if (hasProfileData && contacts.length > 0) status = "green";
          else status = "yellow";
        }
        return {
          athlete: { user_id: uid, full_name: nameMap.get(uid) ?? null },
          status,
          profile,
          contacts,
        };
      });
      built.sort((a, b) => {
        const order: Record<Status, number> = { red: 0, yellow: 1, green: 2 };
        if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
        return (a.athlete.full_name ?? "").localeCompare(b.athlete.full_name ?? "");
      });
      setRows(built);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, user, authLoading]);

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc[r.status]++;
        return acc;
      },
      { green: 0, yellow: 0, red: 0 } as Record<Status, number>,
    );
  }, [rows]);

  if (authLoading || loading) {
    return <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>;
  }

  if (!event) {
    return (
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-20 space-y-4">
        <p className="text-muted-foreground">Evento no encontrado.</p>
        <BackButton onClick={() => navigate({ to: "/dashboard" })} />
      </div>
    );
  }

  if (!access) {
    return (
      <div className="mx-auto max-w-2xl px-4 md:px-6 py-20 space-y-6 text-center">
        <ShieldAlert className="w-16 h-16 mx-auto text-primary" />
        <h1 className="font-display text-3xl">Sin acceso</h1>
        <p className="text-muted-foreground text-lg">
          No tienes acceso al roster de seguridad de este evento.
        </p>
        <BackButton onClick={() => navigate({ to: "/evento/$eventId", params: { eventId } })} />
      </div>
    );
  }

  const dateLabel = new Date(`${event.event_date}T${event.event_time}`).toLocaleString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-8 space-y-6">
      <Link
        to="/evento/$eventId"
        params={{ eventId }}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" /> Volver al evento
      </Link>

      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs uppercase tracking-widest">
          <ShieldAlert className="w-3.5 h-3.5" /> Roster de emergencia
        </div>
        <h1 className="font-display text-4xl break-words">{event.name}</h1>
        <p className="text-muted-foreground">{dateLabel} hrs</p>
      </header>

      {!windowOpen ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <Clock className="w-12 h-12 mx-auto text-primary" />
          <p className="text-lg leading-relaxed">
            El roster de emergencia se activa <strong>1 hora antes</strong> del inicio de la rodada
            y permanece disponible durante el evento. Vuelve más cerca de la hora de salida.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <StatChip label="Ficha completa" count={counts.green} tone="green" />
            <StatChip label="Incompleta" count={counts.yellow} tone="yellow" />
            <StatChip label="Sin autorización" count={counts.red} tone="red" />
          </div>

          {rows.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
              No hay atletas inscritos a este evento.
            </div>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.athlete.user_id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (r.status === "red") return;
                      setSelected(r);
                      if (user) {
                        void supabase.from("ice_access_log").insert({
                          accessed_user_id: r.athlete.user_id,
                          accessed_by: user.id,
                          event_id: eventId,
                          access_type: "roster",
                        });
                      }
                    }}
                    disabled={r.status === "red"}
                    className={`w-full text-left flex items-center gap-4 p-4 rounded-lg border transition min-h-16 ${
                      r.status === "red"
                        ? "border-border bg-card opacity-70 cursor-not-allowed"
                        : "border-border bg-card hover:border-primary hover:bg-primary/5 active:scale-[0.99]"
                    }`}
                  >

                    <StatusIcon status={r.status} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-lg truncate">
                        {r.athlete.full_name || "Deportista"}
                      </div>
                      <div className="text-sm text-muted-foreground">{statusLabel(r.status)}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {selected && (
        <AthleteDetail row={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function statusLabel(s: Status) {
  if (s === "green") return "Ficha completa";
  if (s === "yellow") return "Ficha incompleta";
  return "Sin autorización";
}

function StatusIcon({ status }: { status: Status }) {
  if (status === "green")
    return (
      <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center shrink-0">
        <CheckCircle2 className="w-6 h-6 text-green-400" />
      </div>
    );
  if (status === "yellow")
    return (
      <div className="w-12 h-12 rounded-full bg-yellow-500/20 border-2 border-yellow-500 flex items-center justify-center shrink-0">
        <AlertTriangle className="w-6 h-6 text-yellow-400" />
      </div>
    );
  return (
    <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center shrink-0">
      <XCircle className="w-6 h-6 text-red-400" />
    </div>
  );
}

function StatChip({ label, count, tone }: { label: string; count: number; tone: Status }) {
  const cls =
    tone === "green"
      ? "bg-green-500/10 border-green-500/40 text-green-300"
      : tone === "yellow"
        ? "bg-yellow-500/10 border-yellow-500/40 text-yellow-300"
        : "bg-red-500/10 border-red-500/40 text-red-300";
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="text-3xl font-bold">{count}</div>
      <div className="text-xs uppercase tracking-wider">{label}</div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-5 py-3 min-h-12 rounded-md bg-primary text-primary-foreground font-semibold"
    >
      <ArrowLeft className="w-4 h-4" /> Volver
    </button>
  );
}

function AthleteDetail({ row, onClose }: { row: Row; onClose: () => void }) {
  const { profile, contacts, athlete } = row;
  const sanitizePhone = (p: string) => p.replace(/[^\d+]/g, "");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background border-b border-border px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-display text-2xl truncate">{athlete.full_name || "Deportista"}</h2>
            <p className="text-xs text-muted-foreground uppercase tracking-widest">Ficha de emergencia</p>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 min-h-11 rounded-md bg-muted text-foreground font-semibold"
          >
            Cerrar
          </button>
        </div>

        <div className="p-5 space-y-6">
          <section className="grid grid-cols-2 gap-3">
            <Field label="Tipo de sangre" value={profile?.blood_type} highlight />
            <Field label="Aseguradora" value={profile?.insurance_provider} />
            <Field label="Alergias" value={profile?.allergies} full />
            <Field label="Condiciones médicas" value={profile?.medical_conditions} full />
            <Field label="Medicamentos" value={profile?.medications} full />
            <Field label="No. de póliza" value={profile?.insurance_policy_number} />
            <Field label="Hospital preferido" value={profile?.preferred_hospital} />
            <Field label="Notas" value={profile?.notes} full />
          </section>

          <section className="space-y-3">
            <h3 className="font-display text-xl">Contactos de emergencia</h3>
            {contacts.length === 0 ? (
              <p className="text-muted-foreground">Sin contactos registrados.</p>
            ) : (
              <ul className="space-y-3">
                {contacts.map((c, idx) => {
                  const isPrimary = c.priority === 1 || (idx === 0 && !contacts.some((x) => x.priority === 1));
                  const tel = sanitizePhone(c.phone);
                  const wa = tel.replace(/^\+/, "");
                  return (
                    <li
                      key={c.id}
                      className={`rounded-lg border p-4 ${
                        isPrimary
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card"
                      }`}
                    >
                      {isPrimary && (
                        <div className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-primary text-primary-foreground mb-2">
                          Contacto principal
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-semibold text-lg">{c.full_name}</div>
                        <div className="text-xs uppercase text-muted-foreground">{c.relationship}</div>
                      </div>
                      <div className="text-lg font-mono mt-1">{c.phone}</div>
                      <div className="flex gap-2 mt-3">
                        <a
                          href={`tel:${tel}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 min-h-12 rounded-md bg-primary text-primary-foreground font-semibold"
                        >
                          <Phone className="w-5 h-5" /> Llamar
                        </a>
                        {c.is_whatsapp && (
                          <a
                            href={`https://wa.me/${wa}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 min-h-12 rounded-md bg-green-600 text-white font-semibold"
                          >
                            <MessageCircle className="w-5 h-5" /> WhatsApp
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  full,
  highlight,
}: {
  label: string;
  value: string | null | undefined;
  full?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div
        className={`mt-1 ${
          highlight
            ? "font-display text-3xl text-primary"
            : "text-base whitespace-pre-line"
        } ${!value ? "text-muted-foreground/60 italic" : ""}`}
      >
        {value || "—"}
      </div>
    </div>
  );
}
